import { Separator } from '@heidi/design-system/separator';
import { type Editor, useEditorState } from '@tiptap/react';
import {
  PanelTopDashed,
  TextAlignCenter,
  TextAlignEnd,
  TextAlignStart,
} from 'lucide-react';
import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import {
  type CellAlign,
  getCurrentColumnAlign,
  isHeaderRowActive,
  setColumnAlign,
} from '../core';
import { Toolbar } from './toolbar-namespace';

/**
 * Markdown-native-only table controls (header-row toggle, per-column GFM
 * alignment). The parent gates rendering on `tableCellSupportsAlign` —
 * vMarkdown/v1 schemas can't persist these.
 */
export const MarkdownTableControls = ({ editor }: { editor: Editor }) => {
  const intl = useIntl();
  const { currentAlign, isHeaderRow } = useEditorState({
    editor,
    selector: ({ editor: instance }: { editor: Editor }) => ({
      currentAlign: getCurrentColumnAlign(instance),
      isHeaderRow: isHeaderRowActive(instance),
    }),
  });

  const onToggleHeaderRow = useCallback(() => {
    editor.chain().focus().toggleHeaderRow().run();
  }, [editor]);

  const onSetAlign = useCallback(
    (align: CellAlign) => {
      // Clicking the active alignment clears back to the GFM default (---).
      setColumnAlign(editor, currentAlign === align ? null : align);
    },
    [editor, currentAlign]
  );

  return (
    <>
      <Separator orientation="vertical" className="mx-0.5 h-4" />
      <Toolbar.Button
        tooltip={intl.formatMessage({
          defaultMessage: 'Toggle header row',
          id: 'yy68o/ecQK',
        })}
        onClick={onToggleHeaderRow}
        active={isHeaderRow}
        type="button"
        // The tooltip renders in a portal on hover, so the icon-only button has
        // no accessible name for an e2e locator to reach.
        data-testid="table-menu-toggle-header-row"
      >
        <PanelTopDashed size={14} />
      </Toolbar.Button>
      <Separator orientation="vertical" className="mx-0.5 h-4" />
      <Toolbar.Button
        tooltip={intl.formatMessage({
          defaultMessage: 'Align column left',
          id: 'M4JSV9zRLI',
        })}
        onClick={() => onSetAlign('left')}
        active={currentAlign === 'left'}
        type="button"
      >
        <TextAlignStart size={14} />
      </Toolbar.Button>
      <Toolbar.Button
        tooltip={intl.formatMessage({
          defaultMessage: 'Align column center',
          id: 'ajJ2+sqb1T',
        })}
        onClick={() => onSetAlign('center')}
        active={currentAlign === 'center'}
        type="button"
      >
        <TextAlignCenter size={14} />
      </Toolbar.Button>
      <Toolbar.Button
        tooltip={intl.formatMessage({
          defaultMessage: 'Align column right',
          id: 'mSubxdbRMP',
        })}
        onClick={() => onSetAlign('right')}
        active={currentAlign === 'right'}
        type="button"
      >
        <TextAlignEnd size={14} />
      </Toolbar.Button>
    </>
  );
};
